// AdminRouteTracking.jsx
// Dual map support: OpenStreetMap (Leaflet) + Google Maps
// Provider comes from dashboard user_map (same as RouteMaster)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL } from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* =========================================================
   GOOGLE MAPS LOADER (same pattern as RouteMaster)
========================================================= */

const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;
  return document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");

  if (errorText.includes("RefererNotAllowedMapError")) {
    return "This website is not authorized for the Google Maps API key. Add this domain under the API key website restrictions.";
  }
  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }
  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }
  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }
  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }

  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Google Maps can only be loaded in a browser.")
    );
  }

  if (window.google?.maps) return Promise.resolve(window.google.maps);

  if (!apiKey) {
    return Promise.reject(
      new Error(
        "Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file."
      )
    );
  }

  if (googleMapsLoadingPromise) return googleMapsLoadingPromise;

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;

    const resolveOnce = () => {
      if (settled || !window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;
      reject(
        error instanceof Error
          ? error
          : new Error("Google Maps JavaScript API failed to load.")
      );
    };

    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }
      rejectOnce(
        new Error(
          "Google Maps script loaded, but the Maps API is unavailable."
        )
      );
    };

    const handleScriptError = () => {
      rejectOnce(new Error("Google Maps JavaScript API network request failed."));
    };

    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (callbackError) {
          console.error(
            "Previous Google Maps authentication callback failed:",
            callbackError
          );
        }
      }
      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };

    const existingScript = findExistingGoogleMapsScript();
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      existingScript.addEventListener("error", handleScriptError, {
        once: true,
      });
      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;

    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places,geometry",
      v: "weekly",
    });

    script.src = `https://maps.googleapis.com/maps/api/js?${queryParameters.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");
    script.onload = handleScriptLoad;
    script.onerror = () => {
      script.remove();
      handleScriptError();
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadingPromise;
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();
  if (
    provider === "google" ||
    provider === "googlemaps" ||
    provider === "google_maps"
  ) {
    return "google";
  }
  return "openstreetmap";
}

function getAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
}

/* =========================================================
   TRACKING CONSTANTS / HELPERS
========================================================= */

const ICON = {
  pinSizeFrom: 44,
  pinSizeTo: 44,
  userSize: 48,
  cpRadius: 9,
};

const MAX_ACCURACY_METERS = 50;
const POLL_FAST_MS = 3000;
const COMPLETED_STATUSES = new Set([
  "EARLY",
  "ONTIME",
  "DELAY",
  "COMPLETED",
]);

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? null : ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const value = /Z$|[+-]\d{2}:?\d{2}$/.test(s)
    ? s
    : `${s.replace(" ", "T")}Z`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function durationForMove(fromPt, toPt, fromTs, toTs) {
  const dist = haversineMeters(
    { lat: fromPt[0], lng: fromPt[1] },
    { lat: toPt[0], lng: toPt[1] }
  );
  const fromMs = parseDbIst(fromTs)?.getTime() ?? null;
  const toMs = parseDbIst(toTs)?.getTime() ?? null;
  const realGapMs =
    fromMs != null && toMs != null ? Math.max(500, toMs - fromMs) : 1000;
  const byDistance = Math.min(2200, Math.max(650, dist * 90));
  const byTime = Math.min(1800, Math.max(650, realGapMs * 0.35));
  return Math.round(Math.min(byDistance, byTime));
}

function pointKey(pt, ts = "") {
  return `${Number(pt[0]).toFixed(7)},${Number(pt[1]).toFixed(7)},${ts || ""}`;
}

function formatISTFromUtc(ts) {
  if (!ts) return "—";
  const d =
    ts instanceof Date
      ? ts
      : new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`);
  if (Number.isNaN(d.getTime())) return ts || "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
    .format(d)
    .replace(",", "");
}

function isValidLatLng(p) {
  return (
    p &&
    Number.isFinite(Number(p.lat)) &&
    Number.isFinite(Number(p.lng))
  );
}

function cleanTrack(rawTrack) {
  let points = rawTrack
    .filter((p) => isValidLatLng(p))
    .map((p) => ({
      ...p,
      lat: Number(p.lat),
      lng: Number(p.lng),
      accuracy: p.accuracy == null ? null : Number(p.accuracy),
    }))
    .filter((p) => p.accuracy == null || p.accuracy <= MAX_ACCURACY_METERS);

  const seen = new Set();
  points = points.filter((p) => {
    const key = `${p.lat.toFixed(6)},${p.lng.toFixed(6)},${p.ts}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return points.sort(
    (a, b) =>
      (parseDbIst(a.ts)?.getTime() ?? 0) - (parseDbIst(b.ts)?.getTime() ?? 0)
  );
}

/* Leaflet icons */

function pinSVG(fill = "#111827", stroke = "#111827", size = 40) {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${stroke}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z" fill="${fill}" opacity="0.14"/>
    <circle cx="12" cy="10" r="3.25" fill="${fill}"/>
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z"/>
  </svg>`;
}

function userSVG(color = "#2563EB", size = 44) {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8.5" r="3.5" fill="${color}" opacity="0.12"/>
    <path d="M4 19a8 8 0 0 1 16 0" fill="${color}" opacity="0.08"/>
    <circle cx="12" cy="8.5" r="3.5"/>
    <path d="M4 19a8 8 0 0 1 16 0"/>
  </svg>`;
}

function pinIcon(fill, stroke, size) {
  return L.divIcon({
    className: "pin",
    html: pinSVG(fill, stroke, size),
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

function userIcon(color, size) {
  return L.divIcon({
    className: "userIcon",
    html: `<div class="pulse"></div>${userSVG(color, size)}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function badgeIcon(text, bg, textColor = "#ffffff") {
  return L.divIcon({
    className: "custom-badge",
    html: `<div style="transform:translate(-50%,-50%); background:${bg}; color:${textColor}; font-size:10px; font-weight:700; padding:3px 8px; border-radius:999px; border:2px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.3); white-space:nowrap; text-align:center; min-width:50px;">${text}</div>`,
    iconSize: [60, 24],
    iconAnchor: [30, 12],
  });
}

function loginIcon() {
  return badgeIcon("LOGIN", "#16a34a", "#ffffff");
}

function logoutIcon() {
  return badgeIcon("LOGOUT", "#8b5cf6", "#ffffff");
}

function animateLeafletMarker(marker, targetLatLng, durationMs = 900) {
  return new Promise((resolve) => {
    if (!marker) return resolve();
    const start = marker.getLatLng();
    const dLat = targetLatLng[0] - start.lat;
    const dLng = targetLatLng[1] - start.lng;
    if (Math.abs(dLat) < 0.0000001 && Math.abs(dLng) < 0.0000001) {
      marker.setLatLng(targetLatLng);
      return resolve();
    }
    const startTime = performance.now();
    function step(now) {
      const elapsed = now - startTime;
      const raw = Math.min(elapsed / durationMs, 1);
      const t =
        raw < 0.5
          ? 4 * raw * raw * raw
          : 1 - Math.pow(-2 * raw + 2, 3) / 2;
      marker.setLatLng([start.lat + dLat * t, start.lng + dLng * t]);
      if (raw < 1) requestAnimationFrame(step);
      else resolve();
    }
    requestAnimationFrame(step);
  });
}

function animateGoogleMarker(marker, targetLatLng, durationMs = 900) {
  return new Promise((resolve) => {
    if (!marker || !window.google?.maps) return resolve();
    const start = marker.getPosition();
    if (!start) {
      marker.setPosition({ lat: targetLatLng[0], lng: targetLatLng[1] });
      return resolve();
    }
    const dLat = targetLatLng[0] - start.lat();
    const dLng = targetLatLng[1] - start.lng();
    if (Math.abs(dLat) < 0.0000001 && Math.abs(dLng) < 0.0000001) {
      marker.setPosition({ lat: targetLatLng[0], lng: targetLatLng[1] });
      return resolve();
    }
    const startTime = performance.now();
    function step(now) {
      const elapsed = now - startTime;
      const raw = Math.min(elapsed / durationMs, 1);
      const t =
        raw < 0.5
          ? 4 * raw * raw * raw
          : 1 - Math.pow(-2 * raw + 2, 3) / 2;
      marker.setPosition({
        lat: start.lat() + dLat * t,
        lng: start.lng() + dLng * t,
      });
      if (raw < 1) requestAnimationFrame(step);
      else resolve();
    }
    requestAnimationFrame(step);
  });
}

function googleCircleSymbol(color, scale = 10) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
  };
}

/* =========================================================
   COMPONENT
========================================================= */

export default function AdminRouteTracking() {
  const location = useLocation();

  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState("");
  const [mapLoading, setMapLoading] = useState(false);

  /* Leaflet refs */
  const leafletMapRef = useRef(null);
  const leafletMapElRef = useRef(null);
  const leafletLatestMarkerRef = useRef(null);
  const leafletUserPathRef = useRef(null);
  const leafletLoginLogoutRef = useRef(null);
  const leafletStaticDoneRef = useRef(false);
  const leafletDidFitRef = useRef(false);
  const leafletCheckpointsRef = useRef(null);
  const leafletGeofenceRef = useRef(null);
  const leafletRouteLineRef = useRef(null);
  const leafletEndPointsRef = useRef(null);
  const leafletAnimatedPathRef = useRef(null);

  /* Google refs */
  const googleMapRef = useRef(null);
  const googleMapElRef = useRef(null);
  const googleLatestMarkerRef = useRef(null);
  const googleUserPathRef = useRef(null);
  const googleLoginLogoutRef = useRef([]);
  const googleStaticDoneRef = useRef(false);
  const googleDidFitRef = useRef(false);
  const googleCheckpointsRef = useRef([]);
  const googleGeofenceRef = useRef([]);
  const googleRouteLineRef = useRef(null);
  const googleEndPointsRef = useRef([]);
  const googleAnimatedPathRef = useRef(null);
  const googleInfoWindowRef = useRef(null);

  /* Shared tracking refs */
  const pollTimeoutRef = useRef(null);
  const lastPositionKeyRef = useRef("");
  const isFetchingRef = useRef(false);
  const animationQueueRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const lastAnimatedKeyRef = useRef("");
  const lastBadgeSigRef = useRef("");
  const errorShownRef = useRef(false);
  const lastDataRef = useRef(null);

  const dashboardData = useMemo(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    return {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: normalizeMapProvider(
        localStorage.getItem("user_map") || userData.user_map || ""
      ),
    };
  }, []);

  const isGoogleMap = dashboardData.user_map === "google";

  const routePage = useMemo(
    () => (isGoogleMap ? "/route-master" : "/route-master1"),
    [isGoogleMap]
  );

  const queryData = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      routeId: Number(params.get("route_id") || 0),
      userId: params.get("user_id") || "",
      allocId: Number(params.get("alloc_id") || 0),
    };
  }, [location.search]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      route_id: String(queryData.routeId),
      limit: "5000",
    });
    if (queryData.userId) params.set("user_id", queryData.userId);
    if (queryData.allocId) params.set("alloc_id", String(queryData.allocId));
    return `${API_BASE_URL}/routeTracking?${params.toString()}`;
  }, [queryData]);

  const setStatus = useCallback((id, text, type = "neutral") => {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = text;
    const base = "px-3 py-1 text-xs font-semibold rounded-full";
    const classes = {
      ok: "bg-green-100 text-green-700",
      warn: "bg-red-100 text-red-700",
      neutral: "bg-slate-200 text-slate-800",
    };
    el.className = `${base} ${classes[type] || classes.neutral}`;
  }, []);

  const statusSet = useCallback(
    (t, c = "neutral") => setStatus("fenceStatus", t, c),
    [setStatus]
  );

  const routeStatusSet = useCallback(
    (t, c = "neutral") => setStatus("routeStatus", t, c),
    [setStatus]
  );

  /* ---------- destroy helpers ---------- */

  const destroyLeaflet = useCallback(() => {
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
    }
    leafletLatestMarkerRef.current = null;
    leafletUserPathRef.current = null;
    leafletLoginLogoutRef.current = null;
    leafletStaticDoneRef.current = false;
    leafletDidFitRef.current = false;
    leafletCheckpointsRef.current = null;
    leafletGeofenceRef.current = null;
    leafletRouteLineRef.current = null;
    leafletEndPointsRef.current = null;
    leafletAnimatedPathRef.current = null;
  }, []);

  const destroyGoogle = useCallback(() => {
    googleLoginLogoutRef.current.forEach((m) => m?.setMap(null));
    googleLoginLogoutRef.current = [];
    googleCheckpointsRef.current.forEach((m) => m?.setMap(null));
    googleCheckpointsRef.current = [];
    googleGeofenceRef.current.forEach((m) => m?.setMap(null));
    googleGeofenceRef.current = [];
    googleEndPointsRef.current.forEach((m) => m?.setMap(null));
    googleEndPointsRef.current = [];
    if (googleRouteLineRef.current) {
      googleRouteLineRef.current.setMap(null);
      googleRouteLineRef.current = null;
    }
    if (googleUserPathRef.current) {
      googleUserPathRef.current.setMap(null);
      googleUserPathRef.current = null;
    }
    if (googleAnimatedPathRef.current) {
      googleAnimatedPathRef.current.setMap(null);
      googleAnimatedPathRef.current = null;
    }
    if (googleLatestMarkerRef.current) {
      googleLatestMarkerRef.current.setMap(null);
      googleLatestMarkerRef.current = null;
    }
    if (googleInfoWindowRef.current) {
      googleInfoWindowRef.current.close();
      googleInfoWindowRef.current = null;
    }
    googleMapRef.current = null;
    googleStaticDoneRef.current = false;
    googleDidFitRef.current = false;
  }, []);

  /* ---------- LEAFLET static / live ---------- */

  const drawLeafletStatic = useCallback((data) => {
    const map = leafletMapRef.current;
    if (!map || leafletStaticDoneRef.current) return;

    const bounds = L.latLngBounds();
    const from = data.route?.meta?.from || null;
    const to = data.route?.meta?.to || null;
    const endMarkers = [];

    if (isValidLatLng(from)) {
      const lat = Number(from.lat);
      const lng = Number(from.lng);
      endMarkers.push(
        L.marker([lat, lng], {
          icon: pinIcon("#16a34a", "#166534", ICON.pinSizeFrom),
        }).bindTooltip("From", { direction: "top", className: "custom-tooltip" })
      );
      bounds.extend([lat, lng]);
    }

    if (isValidLatLng(to)) {
      const lat = Number(to.lat);
      const lng = Number(to.lng);
      endMarkers.push(
        L.marker([lat, lng], {
          icon: pinIcon("#dc2626", "#991b1b", ICON.pinSizeTo),
        }).bindTooltip("To", { direction: "top", className: "custom-tooltip" })
      );
      bounds.extend([lat, lng]);
    }

    if (endMarkers.length) {
      leafletEndPointsRef.current = L.layerGroup(endMarkers).addTo(map);
    }

    const routeLine = data.route?.line?.geometry?.coordinates || [];
    if (Array.isArray(routeLine) && routeLine.length >= 2) {
      const coordsLL = routeLine
        .map(([lng, lat]) => [Number(lat), Number(lng)])
        .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));
      if (coordsLL.length >= 2) {
        leafletRouteLineRef.current = L.polyline(coordsLL, {
          color: "#0ea5e9",
          weight: 4,
          opacity: 0.75,
          dashArray: "5,7",
        }).addTo(map);
        coordsLL.forEach((c) => bounds.extend(c));
      }
    }

    const cps = Array.isArray(data.checkpoints) ? data.checkpoints : [];
    const cpMarkers = cps
      .filter((cp) => isValidLatLng(cp))
      .map((cp) => {
        const lat = Number(cp.lat);
        const lng = Number(cp.lng);
        bounds.extend([lat, lng]);
        return L.circleMarker([lat, lng], {
          radius: ICON.cpRadius,
          color: "#16a34a",
          weight: 2,
          fillColor: "#22c55e",
          fillOpacity: 0.35,
        }).bindTooltip(cp.checkpoint_name || "Checkpoint", {
          direction: "top",
          className: "custom-tooltip",
        });
      });
    if (cpMarkers.length) {
      leafletCheckpointsRef.current = L.layerGroup(cpMarkers).addTo(map);
    }

    const gf = data.geofences?.features || [];
    const gfShapes = [];
    gf.forEach((f) => {
      const p = f.properties || {};
      if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
        const [lngR, latR] = f.geometry.coordinates || [];
        const lat = Number(latR);
        const lng = Number(lngR);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          gfShapes.push(
            L.circle([lat, lng], {
              radius: Number(p.radius_m || 0),
              color: "#f59e0b",
              weight: 2,
              fillOpacity: 0.08,
            }).bindPopup(
              `<div class="custom-popup"><b>${p.name || "Fence"}</b><br>Circle ${p.radius_m || 0}m</div>`
            )
          );
          bounds.extend([lat, lng]);
        }
      }
      if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
        const ring = (f.geometry.coordinates?.[0] || [])
          .map(([ln, la]) => [Number(la), Number(ln)])
          .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));
        if (ring.length) {
          gfShapes.push(
            L.polygon(ring, {
              color: "#ef4444",
              weight: 2,
              fillOpacity: 0.06,
            }).bindPopup(
              `<div class="custom-popup"><b>${p.name || "Fence"}</b><br>Polygon</div>`
            )
          );
          ring.forEach((c) => bounds.extend(c));
        }
      }
    });
    if (gfShapes.length) {
      leafletGeofenceRef.current = L.layerGroup(gfShapes).addTo(map);
    }

    leafletStaticDoneRef.current = true;
    if (bounds.isValid() && !leafletDidFitRef.current) {
      map.fitBounds(bounds.pad(0.15));
      leafletDidFitRef.current = true;
    }
  }, []);

  const runLeafletAnimationQueue = useCallback(async () => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    try {
      while (animationQueueRef.current.length) {
        const item = animationQueueRef.current.shift();
        if (leafletLatestMarkerRef.current) {
          await animateLeafletMarker(
            leafletLatestMarkerRef.current,
            item.pt,
            item.durationMs
          );
          leafletLatestMarkerRef.current.setTooltipContent(item.tooltip || "");
          if (
            leafletAnimatedPathRef.current &&
            typeof leafletAnimatedPathRef.current.addLatLng === "function"
          ) {
            leafletAnimatedPathRef.current.addLatLng(item.pt);
          }
        }
        lastAnimatedKeyRef.current = item.key;
      }
    } finally {
      isAnimatingRef.current = false;
    }
  }, []);

  const drawLeafletLive = useCallback(
    (data) => {
      const map = leafletMapRef.current;
      if (!map) return;

      const guards = data.gps?.guards || [];
      const completion = data.completion || null;
      const isCompleted = COMPLETED_STATUSES.has(
        String(completion?.status || "").toUpperCase()
      );

      let latestTsRaw = null;

      for (const g of guards) {
        const rawTrack = Array.isArray(g.track) ? g.track : [];
        const trackingStartMs =
          parseDbIst(data?.tracking?.start_time)?.getTime() ?? null;

        const orderedTrack = cleanTrack(rawTrack).filter((p) => {
          if (!trackingStartMs) return true;
          return (parseDbIst(p.ts)?.getTime() ?? 0) >= trackingStartMs - 30000;
        });

        if (!orderedTrack.length) continue;

        const allPoints = orderedTrack.map((p) => [p.lat, p.lng]);
        const firstPt = allPoints[0];
        const lastPt = allPoints[allPoints.length - 1];
        const firstTs = orderedTrack[0].ts;
        const lastTs = orderedTrack[orderedTrack.length - 1].ts;

        const loginPt = [
          Number(data?.tracking?.start_latitude ?? firstPt[0]),
          Number(data?.tracking?.start_longitude ?? firstPt[1]),
        ];

        const badgeSig = `${firstTs}-${
          isCompleted ? completion?.completed_at || lastTs : "ACTIVE"
        }`;

        if (
          badgeSig !== lastBadgeSigRef.current ||
          !leafletLoginLogoutRef.current
        ) {
          if (leafletLoginLogoutRef.current) {
            map.removeLayer(leafletLoginLogoutRef.current);
          }
          const markers = [
            L.marker(loginPt, { icon: loginIcon() }).bindTooltip(
              `Login<br>${formatISTFromUtc(
                data?.tracking?.start_time || firstTs
              )}`,
              { direction: "top", className: "custom-tooltip" }
            ),
          ];
          if (isCompleted) {
            markers.push(
              L.marker(lastPt, { icon: logoutIcon() }).bindTooltip(
                `Logout<br>${formatISTFromUtc(
                  completion?.completed_at || lastTs
                )}`,
                { direction: "top", className: "custom-tooltip" }
              )
            );
          }
          leafletLoginLogoutRef.current = L.layerGroup(markers).addTo(map);
          lastBadgeSigRef.current = badgeSig;
        }

        if (isCompleted) {
          if (leafletUserPathRef.current) map.removeLayer(leafletUserPathRef.current);
          if (leafletAnimatedPathRef.current)
            map.removeLayer(leafletAnimatedPathRef.current);
          leafletUserPathRef.current = L.polyline(allPoints, {
            weight: 6,
            opacity: 0.92,
            color: "#0ea5e9",
            lineJoin: "round",
            lineCap: "round",
          }).addTo(map);
          if (leafletLatestMarkerRef.current) {
            map.removeLayer(leafletLatestMarkerRef.current);
            leafletLatestMarkerRef.current = null;
          }
        } else {
          const tooltip = `Name: ${
            g.guard_name || g.guard_id || "Guard"
          }<br>Time: ${formatISTFromUtc(lastTs)}`;

          if (!leafletLatestMarkerRef.current) {
            leafletLatestMarkerRef.current = L.marker(loginPt, {
              icon: userIcon("#2563EB", ICON.userSize),
              zIndexOffset: 1200,
            })
              .bindTooltip(tooltip, {
                direction: "top",
                className: "custom-tooltip",
              })
              .addTo(map);

            leafletAnimatedPathRef.current = L.polyline([loginPt], {
              weight: 5,
              opacity: 0.95,
              color: "#0ea5e9",
              smoothFactor: 1,
            }).addTo(map);
          } else {
            leafletLatestMarkerRef.current.setTooltipContent(tooltip);
          }

          const pointItems = orderedTrack.map((p, idx) => ({
            pt: [p.lat, p.lng],
            key: pointKey([p.lat, p.lng], p.ts),
            prev:
              idx > 0
                ? [orderedTrack[idx - 1].lat, orderedTrack[idx - 1].lng]
                : [p.lat, p.lng],
            prevTs: idx > 0 ? orderedTrack[idx - 1].ts : p.ts,
            ts: p.ts,
            tooltip,
          }));

          const lastIdx = pointItems.findIndex(
            (x) => x.key === lastAnimatedKeyRef.current
          );
          const newItems =
            lastIdx >= 0 ? pointItems.slice(lastIdx + 1) : pointItems;

          newItems.forEach((item) => {
            if (!animationQueueRef.current.some((q) => q.key === item.key)) {
              animationQueueRef.current.push({
                ...item,
                durationMs: durationForMove(
                  item.prev,
                  item.pt,
                  item.prevTs,
                  item.ts
                ),
              });
            }
          });

          runLeafletAnimationQueue();
        }

        latestTsRaw = lastTs;
        if (pointKey(lastPt, lastTs) !== lastPositionKeyRef.current) {
          lastPositionKeyRef.current = pointKey(lastPt, lastTs);
        }
      }

      const lastTsEl = document.getElementById("lastTs");
      if (lastTsEl) {
        lastTsEl.textContent = latestTsRaw
          ? `Last update: ${formatISTFromUtc(latestTsRaw)}`
          : "Last update: —";
      }

      routeStatusSet(
        isCompleted ? "Route status: Completed" : "Route status: In progress",
        isCompleted ? "ok" : "neutral"
      );
    },
    [runLeafletAnimationQueue, routeStatusSet]
  );

  /* ---------- GOOGLE static / live ---------- */

  const drawGoogleStatic = useCallback((data) => {
    const map = googleMapRef.current;
    if (!map || !window.google?.maps || googleStaticDoneRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    const from = data.route?.meta?.from || null;
    const to = data.route?.meta?.to || null;

    if (isValidLatLng(from)) {
      const pos = { lat: Number(from.lat), lng: Number(from.lng) };
      const m = new window.google.maps.Marker({
        map,
        position: pos,
        title: "From",
        icon: googleCircleSymbol("#16a34a", 11),
      });
      googleEndPointsRef.current.push(m);
      bounds.extend(pos);
    }

    if (isValidLatLng(to)) {
      const pos = { lat: Number(to.lat), lng: Number(to.lng) };
      const m = new window.google.maps.Marker({
        map,
        position: pos,
        title: "To",
        icon: googleCircleSymbol("#dc2626", 11),
      });
      googleEndPointsRef.current.push(m);
      bounds.extend(pos);
    }

    const routeLine = data.route?.line?.geometry?.coordinates || [];
    if (Array.isArray(routeLine) && routeLine.length >= 2) {
      const path = routeLine
        .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
      if (path.length >= 2) {
        googleRouteLineRef.current = new window.google.maps.Polyline({
          map,
          path,
          strokeColor: "#0ea5e9",
          strokeOpacity: 0.75,
          strokeWeight: 4,
          icons: [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 1,
                scale: 3,
              },
              offset: "0",
              repeat: "12px",
            },
          ],
        });
        path.forEach((p) => bounds.extend(p));
      }
    }

    const cps = Array.isArray(data.checkpoints) ? data.checkpoints : [];
    cps.filter(isValidLatLng).forEach((cp) => {
      const pos = { lat: Number(cp.lat), lng: Number(cp.lng) };
      const m = new window.google.maps.Marker({
        map,
        position: pos,
        title: cp.checkpoint_name || "Checkpoint",
        icon: googleCircleSymbol("#22c55e", 8),
      });
      googleCheckpointsRef.current.push(m);
      bounds.extend(pos);
    });

    const gf = data.geofences?.features || [];
    gf.forEach((f) => {
      const p = f.properties || {};
      if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
        const [lngR, latR] = f.geometry.coordinates || [];
        const lat = Number(latR);
        const lng = Number(lngR);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const circle = new window.google.maps.Circle({
            map,
            center: { lat, lng },
            radius: Number(p.radius_m || 0),
            strokeColor: "#f59e0b",
            strokeWeight: 2,
            fillColor: "#f59e0b",
            fillOpacity: 0.08,
          });
          googleGeofenceRef.current.push(circle);
          bounds.extend({ lat, lng });
        }
      }
      if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
        const path = (f.geometry.coordinates?.[0] || [])
          .map(([ln, la]) => ({ lat: Number(la), lng: Number(ln) }))
          .filter((x) => Number.isFinite(x.lat) && Number.isFinite(x.lng));
        if (path.length) {
          const poly = new window.google.maps.Polygon({
            map,
            paths: path,
            strokeColor: "#ef4444",
            strokeWeight: 2,
            fillColor: "#ef4444",
            fillOpacity: 0.06,
          });
          googleGeofenceRef.current.push(poly);
          path.forEach((pt) => bounds.extend(pt));
        }
      }
    });

    googleStaticDoneRef.current = true;
    if (!bounds.isEmpty() && !googleDidFitRef.current) {
      map.fitBounds(bounds, 48);
      googleDidFitRef.current = true;
    }
  }, []);

  const runGoogleAnimationQueue = useCallback(async () => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    try {
      while (animationQueueRef.current.length) {
        const item = animationQueueRef.current.shift();
        if (googleLatestMarkerRef.current) {
          await animateGoogleMarker(
            googleLatestMarkerRef.current,
            item.pt,
            item.durationMs
          );
          if (googleInfoWindowRef.current) {
            googleInfoWindowRef.current.setContent(
              `<div style="color:#1a1a1a;font-size:12px;font-weight:600">${item.tooltip || ""}</div>`
            );
          }
          if (googleAnimatedPathRef.current) {
            const path = googleAnimatedPathRef.current.getPath();
            path.push(
              new window.google.maps.LatLng(item.pt[0], item.pt[1])
            );
          }
        }
        lastAnimatedKeyRef.current = item.key;
      }
    } finally {
      isAnimatingRef.current = false;
    }
  }, []);

  const drawGoogleLive = useCallback(
    (data) => {
      const map = googleMapRef.current;
      if (!map || !window.google?.maps) return;

      const guards = data.gps?.guards || [];
      const completion = data.completion || null;
      const isCompleted = COMPLETED_STATUSES.has(
        String(completion?.status || "").toUpperCase()
      );

      let latestTsRaw = null;

      for (const g of guards) {
        const rawTrack = Array.isArray(g.track) ? g.track : [];
        const trackingStartMs =
          parseDbIst(data?.tracking?.start_time)?.getTime() ?? null;

        const orderedTrack = cleanTrack(rawTrack).filter((p) => {
          if (!trackingStartMs) return true;
          return (parseDbIst(p.ts)?.getTime() ?? 0) >= trackingStartMs - 30000;
        });

        if (!orderedTrack.length) continue;

        const allPoints = orderedTrack.map((p) => ({
          lat: p.lat,
          lng: p.lng,
        }));
        const firstPt = [orderedTrack[0].lat, orderedTrack[0].lng];
        const lastPt = [
          orderedTrack[orderedTrack.length - 1].lat,
          orderedTrack[orderedTrack.length - 1].lng,
        ];
        const firstTs = orderedTrack[0].ts;
        const lastTs = orderedTrack[orderedTrack.length - 1].ts;

        const loginPt = {
          lat: Number(data?.tracking?.start_latitude ?? firstPt[0]),
          lng: Number(data?.tracking?.start_longitude ?? firstPt[1]),
        };

        const badgeSig = `${firstTs}-${
          isCompleted ? completion?.completed_at || lastTs : "ACTIVE"
        }`;

        if (badgeSig !== lastBadgeSigRef.current) {
          googleLoginLogoutRef.current.forEach((m) => m?.setMap(null));
          googleLoginLogoutRef.current = [];

          const loginMarker = new window.google.maps.Marker({
            map,
            position: loginPt,
            title: `Login ${formatISTFromUtc(
              data?.tracking?.start_time || firstTs
            )}`,
            label: {
              text: "IN",
              color: "#ffffff",
              fontSize: "10px",
              fontWeight: "700",
            },
            icon: googleCircleSymbol("#16a34a", 12),
          });
          googleLoginLogoutRef.current.push(loginMarker);

          if (isCompleted) {
            const logoutMarker = new window.google.maps.Marker({
              map,
              position: { lat: lastPt[0], lng: lastPt[1] },
              title: `Logout ${formatISTFromUtc(
                completion?.completed_at || lastTs
              )}`,
              label: {
                text: "OUT",
                color: "#ffffff",
                fontSize: "10px",
                fontWeight: "700",
              },
              icon: googleCircleSymbol("#8b5cf6", 12),
            });
            googleLoginLogoutRef.current.push(logoutMarker);
          }

          lastBadgeSigRef.current = badgeSig;
        }

        if (isCompleted) {
          if (googleAnimatedPathRef.current) {
            googleAnimatedPathRef.current.setMap(null);
            googleAnimatedPathRef.current = null;
          }
          if (googleUserPathRef.current) {
            googleUserPathRef.current.setMap(null);
          }
          googleUserPathRef.current = new window.google.maps.Polyline({
            map,
            path: allPoints,
            strokeColor: "#0ea5e9",
            strokeOpacity: 0.92,
            strokeWeight: 6,
          });
          if (googleLatestMarkerRef.current) {
            googleLatestMarkerRef.current.setMap(null);
            googleLatestMarkerRef.current = null;
          }
        } else {
          const tooltipHtml = `Name: ${
            g.guard_name || g.guard_id || "Guard"
          }<br>Time: ${formatISTFromUtc(lastTs)}`;

          if (!googleLatestMarkerRef.current) {
            googleLatestMarkerRef.current = new window.google.maps.Marker({
              map,
              position: loginPt,
              title: "Live guard",
              icon: googleCircleSymbol("#2563EB", 12),
              zIndex: 999,
            });

            if (!googleInfoWindowRef.current) {
              googleInfoWindowRef.current = new window.google.maps.InfoWindow();
            }
            googleInfoWindowRef.current.setContent(
              `<div style="color:#1a1a1a;font-size:12px;font-weight:600">${tooltipHtml}</div>`
            );
            googleInfoWindowRef.current.open(
              map,
              googleLatestMarkerRef.current
            );

            googleAnimatedPathRef.current = new window.google.maps.Polyline({
              map,
              path: [loginPt],
              strokeColor: "#0ea5e9",
              strokeOpacity: 0.95,
              strokeWeight: 5,
            });
          } else if (googleInfoWindowRef.current) {
            googleInfoWindowRef.current.setContent(
              `<div style="color:#1a1a1a;font-size:12px;font-weight:600">${tooltipHtml}</div>`
            );
          }

          const pointItems = orderedTrack.map((p, idx) => ({
            pt: [p.lat, p.lng],
            key: pointKey([p.lat, p.lng], p.ts),
            prev:
              idx > 0
                ? [orderedTrack[idx - 1].lat, orderedTrack[idx - 1].lng]
                : [p.lat, p.lng],
            prevTs: idx > 0 ? orderedTrack[idx - 1].ts : p.ts,
            ts: p.ts,
            tooltip: tooltipHtml,
          }));

          const lastIdx = pointItems.findIndex(
            (x) => x.key === lastAnimatedKeyRef.current
          );
          const newItems =
            lastIdx >= 0 ? pointItems.slice(lastIdx + 1) : pointItems;

          newItems.forEach((item) => {
            if (!animationQueueRef.current.some((q) => q.key === item.key)) {
              animationQueueRef.current.push({
                ...item,
                durationMs: durationForMove(
                  item.prev,
                  item.pt,
                  item.prevTs,
                  item.ts
                ),
              });
            }
          });

          runGoogleAnimationQueue();
        }

        latestTsRaw = lastTs;
        if (pointKey(lastPt, lastTs) !== lastPositionKeyRef.current) {
          lastPositionKeyRef.current = pointKey(lastPt, lastTs);
        }
      }

      const lastTsEl = document.getElementById("lastTs");
      if (lastTsEl) {
        lastTsEl.textContent = latestTsRaw
          ? `Last update: ${formatISTFromUtc(latestTsRaw)}`
          : "Last update: —";
      }

      routeStatusSet(
        isCompleted ? "Route status: Completed" : "Route status: In progress",
        isCompleted ? "ok" : "neutral"
      );
    },
    [runGoogleAnimationQueue, routeStatusSet]
  );

  /* ---------- fetch / poll ---------- */

  const refresh = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("Login token missing. Please login again.");
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        throw new Error(data?.message || "Unauthorized. Please login again.");
      }

      if (!response.ok || !(data.ok || data.success)) {
        throw new Error(
          data?.error || data?.message || `HTTP ${response.status}`
        );
      }

      errorShownRef.current = false;
      lastDataRef.current = data;

      if (isGoogleMap) {
        drawGoogleStatic(data);
        drawGoogleLive(data);
      } else {
        drawLeafletStatic(data);
        drawLeafletLive(data);
      }
    } catch (error) {
      console.error("[routeTracking] fetch failed:", error);
      statusSet(error.message || "API error", "warn");
      if (!errorShownRef.current) {
        toast.error(error.message || "Failed to fetch tracking data");
        errorShownRef.current = true;
      }
    } finally {
      isFetchingRef.current = false;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(refresh, POLL_FAST_MS);
    }
  }, [
    apiUrl,
    isGoogleMap,
    drawGoogleStatic,
    drawGoogleLive,
    drawLeafletStatic,
    drawLeafletLive,
    statusSet,
  ]);

  /* ---------- init maps ---------- */

  useEffect(() => {
    if (!queryData.routeId) return undefined;

    let cancelled = false;
    setMapError("");
    setMapReady(false);
    lastPositionKeyRef.current = "";
    animationQueueRef.current = [];
    isAnimatingRef.current = false;
    lastAnimatedKeyRef.current = "";
    lastBadgeSigRef.current = "";
    errorShownRef.current = false;

    const startPolling = () => {
      if (!cancelled) {
        setMapReady(true);
        refresh();
      }
    };

    if (isGoogleMap) {
      destroyLeaflet();
      setMapLoading(true);

      (async () => {
        try {
          await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
          if (cancelled || !googleMapElRef.current) return;

          const map = new window.google.maps.Map(googleMapElRef.current, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            clickableIcons: false,
          });
          googleMapRef.current = map;
          setMapLoading(false);
          startPolling();
        } catch (error) {
          console.error("Google Maps init error:", error);
          const message = getGoogleMapsErrorMessage(error);
          setMapError(message);
          setMapLoading(false);
          toast.error(message);
        }
      })();
    } else {
      destroyGoogle();
      if (!leafletMapElRef.current) return undefined;

      const map = L.map(leafletMapElRef.current, {
        zoomControl: true,
      }).setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
      leafletMapRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 20,
        attribution: "&copy; OpenStreetMap",
      }).addTo(map);

      L.control.scale({ imperial: false }).addTo(map);

      const legend = L.control({ position: "bottomright" });
      legend.onAdd = () => {
        const d = L.DomUtil.create("div", "legend");
        d.innerHTML = `
          <div><span class="chip" style="background:#0ea5e9"></span>Track</div>
          <div><span class="chip" style="background:#ef4444"></span>Fence (Polygon)</div>
          <div><span class="chip" style="background:#f59e0b"></span>Fence (Circle)</div>
          <div><span class="chip" style="background:#22c55e"></span>Checkpoints</div>
          <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:3px;"></span> From</div>
          <div><span style="display:inline-block;width:14px;height:14px;background:#dc2626;border-radius:3px;"></span> To</div>
          <div><span style="display:inline-block;width:14px;height:14px;background:#2563EB;border-radius:50%;"></span> Live user</div>
          <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:999px;"></span> Login</div>
          <div><span style="display:inline-block;width:14px;height:14px;background:#8b5cf6;border-radius:999px;"></span> Logout</div>`;
        return d;
      };
      legend.addTo(map);

      startPolling();
    }

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      destroyLeaflet();
      destroyGoogle();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData.routeId, isGoogleMap]);

  if (!queryData.routeId) {
    return (
      <Layout
        dashboardData={dashboardData}
        routePage={routePage}
        activePage="tracking"
        activeMenu="tracking"
      >
        <Toaster position="top-right" />
        <div className="p-5 text-[var(--text-primary)] bg-[var(--card-bg)] rounded-2xl border border-[var(--card-border)] shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <h2 className="text-[18px] font-extrabold text-[var(--text-primary)]">
            Route ID Required
          </h2>
          <p className="text-[13px] text-[var(--text-muted)]">
            Example: <b>?route_id=123</b>
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="tracking"
      activeMenu="tracking"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
        }}
      />

      <style>{`
        #map, .tracking-map-el {
          height: 100%;
          width: 100%;
          min-height: 520px;
        }
        .legend {
          background: #fff;
          padding: 10px 12px;
          border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,.12);
          font: 12px/1.25 system-ui;
          color: #1a1a1a;
        }
        .legend div {
          margin: 6px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .chip {
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 3px;
        }
        .pin { transform: translate(-50%, -100%); }
        .userIcon { transform: translate(-50%, -50%); }
        .custom-badge {
          background: transparent !important;
          box-shadow: none !important;
        }
        .custom-tooltip {
          background: #ffffff !important;
          color: #1a1a1a !important;
          border: 1px solid #d1d5db !important;
          border-radius: 8px !important;
          padding: 6px 12px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
        }
        .custom-tooltip::before { border-top-color: #ffffff !important; }
        .custom-popup, .leaflet-popup-content {
          color: #1a1a1a !important;
        }
        .pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          transform: translate(-50%, -50%);
          animation: pulse 1.6s infinite;
        }
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(37,99,235,.55); }
          70% { box-shadow: 0 0 0 18px rgba(37,99,235,0); }
          100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
        }
      `}</style>

      <div className="tracking-container h-[calc(100vh-90px)] min-h-[650px] bg-[var(--page-bg)] rounded-2xl overflow-hidden border border-[var(--card-border)] shadow-[0_8px_32px_rgba(0,0,0,0.3)] flex flex-col">
        <header className="tracking-header bg-[var(--card-bg)] border-b border-[var(--divider)] px-4 py-3 flex flex-wrap items-center gap-3">
          <h3 className="m-0 text-lg font-extrabold text-[var(--text-primary)]">
            Route #{queryData.routeId}
            {queryData.allocId ? ` — Allocation #${queryData.allocId}` : ""}
          </h3>

          <div
            className="px-3 py-1 text-xs font-extrabold rounded-full bg-[var(--surface)] text-[var(--text-primary)] border border-[var(--surface-border)]"
            title="Map provider from company user_map setting"
          >
            {isGoogleMap ? "Google Maps" : "OpenStreetMap"}
          </div>

          <div id="guardInfo" className="text-sm text-[var(--text-secondary)]">
            Guard: —
          </div>

          <div id="sessionRange" className="text-xs text-[var(--text-muted)]">
            Session: —
          </div>

          <div
            id="routeStatus"
            className="px-3 py-1 text-xs font-extrabold rounded-full bg-[var(--surface)] text-[var(--text-primary)]"
          >
            Route status: —
          </div>

          <div
            id="fenceStatus"
            className="px-3 py-1 text-xs font-extrabold rounded-full bg-[var(--surface)] text-[var(--text-primary)]"
          >
            No fence
          </div>

          <div id="lastTs" className="ml-auto text-xs text-[var(--text-faint)]">
            —
          </div>

          <div
            id="pollIndicator"
            className="px-2 py-1 text-xs font-extrabold rounded-full bg-[var(--accent)] text-white"
          >
            ↻ 3.0s
          </div>
        </header>

        <div className="flex-1 relative">
          {!isGoogleMap && (
            <div
              id="map"
              ref={leafletMapElRef}
              className="tracking-map-el absolute inset-0"
            />
          )}
          {isGoogleMap && (
            <div
              ref={googleMapElRef}
              className="tracking-map-el absolute inset-0"
            />
          )}

          {mapLoading && (
            <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
              <p className="text-sm font-extrabold text-[var(--text-primary)]">
                Loading Google Maps...
              </p>
            </div>
          )}

          {mapError && (
            <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--surface)] p-5">
              <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 text-center shadow-xl">
                <p className="text-sm font-extrabold text-red-700">
                  Map could not be loaded
                </p>
                <p className="mt-2 text-xs font-semibold text-red-600">
                  {mapError}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
